import React from 'react';
import { NavLink, useParams, useLocation } from 'react-router-dom';
import { Stack, Button, IconButton, Tooltip } from '@mui/material';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { useAuth } from '../../contexts/AuthContext';

const LINKS = [
  { path: 'profile', label: 'My Profile' },
  { path: 'pledges', label: 'Pledges' },
  { path: 'donations', label: 'Donations' },
  { path: 'statements', label: 'Statements' },
  { path: 'donate', label: 'Donate', href: '/donate' },
];

/**
 * @param {{ disabled?: boolean, onContactClick?: () => void }} props
 */
export default function PortalTopNav({ disabled = false, onContactClick }) {
  const { id: routeDevoteeId } = useParams();
  const location = useLocation();
  const { staff } = useAuth();
  const onDonateRoute = location.pathname.startsWith('/donate') || location.pathname.startsWith('/product/');
  const onLoginRoute = location.pathname === '/login' || location.pathname.startsWith('/login/');
  const subject =
    routeDevoteeId != null && String(routeDevoteeId).trim() !== ''
      ? String(routeDevoteeId)
      : staff?.portal && staff?.subject != null
        ? String(staff.subject)
        : null;
  const base = subject != null ? `/devotees/${subject}` : null;
  const showPortalLinks = !!base;
  const showDonateLink = showPortalLinks && !onDonateRoute;
  const showPublicDonateLink = onLoginRoute;
  // Permanent in-app Home icon on every donate-app route (public or portal), so
  // users can always return to the /donate landing. (The ICCT parent site is
  // reachable via the header logo, so no separate external button is needed.)
  const showDonateHome = onDonateRoute;
  const showContact =
    typeof onContactClick === 'function' && (showPortalLinks || onDonateRoute || onLoginRoute);

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 0, columnGap: 1, rowGap: 1, alignItems: 'center' }}
    >
      {showDonateHome ? (
        <Tooltip title="Donate home">
          <span>
            <IconButton
              component={NavLink}
              to={disabled ? location.pathname : '/donate'}
              end
              aria-label="Donate home"
              color="primary"
              size="small"
              disabled={disabled}
              sx={{ border: '2px solid', borderColor: 'primary.main', borderRadius: 1 }}
            >
              <HomeRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      {showDonateHome ? (
        <NavLink
          to={disabled ? location.pathname : '/donate/kotilinga'}
          style={{ textDecoration: 'none' }}
          onClick={disabled ? (e) => e.preventDefault() : undefined}
        >
          {({ isActive }) => (
            <Button
              variant={isActive && !disabled ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              disabled={disabled}
              sx={{ fontWeight: 700, borderWidth: 2 }}
            >
              Kotilinga Kshetram
            </Button>
          )}
        </NavLink>
      ) : null}
      {showPublicDonateLink ? (
        <NavLink to={disabled ? location.pathname : '/donate'} style={{ textDecoration: 'none' }}>
          {({ isActive }) => (
            <Button
              variant={isActive && !disabled ? 'contained' : 'outlined'}
              color="secondary"
              size="small"
              disabled={disabled}
              sx={{ fontWeight: 700, borderWidth: 2 }}
            >
              Donate
            </Button>
          )}
        </NavLink>
      ) : null}
      {LINKS.map(({ path, label, href }) => {
        const isDonate = path === 'donate';
        if (isDonate && !showDonateLink) return null;
        if (!isDonate && !showPortalLinks) return null;
        const to = href || `${base}/${path}`;
        if (isDonate) {
          return (
            <NavLink
              key={path}
              to={disabled ? location.pathname : '/donate'}
              style={{ textDecoration: 'none' }}
              onClick={disabled ? (e) => e.preventDefault() : undefined}
            >
              {({ isActive }) => (
                <Button
                  variant={isActive && !disabled ? 'contained' : 'outlined'}
                  color={isActive && !disabled ? 'primary' : 'secondary'}
                  size="small"
                  disabled={disabled}
                  sx={{
                    fontWeight: 700,
                    textDecoration: 'none',
                    borderWidth: 2,
                  }}
                >
                  {label}
                </Button>
              )}
            </NavLink>
          );
        }
        return (
          <NavLink
            key={path}
            to={disabled ? location.pathname : to}
            style={{ textDecoration: 'none' }}
            onClick={disabled ? (e) => e.preventDefault() : undefined}
          >
            {({ isActive }) => (
              <Button
                variant={isActive && !disabled ? 'contained' : 'outlined'}
                color="primary"
                size="small"
                disabled={disabled}
              >
                {label}
              </Button>
            )}
          </NavLink>
        );
      })}
      {showContact ? (
        <Tooltip title="Contact us">
          <span>
            <IconButton
              aria-label="Contact us"
              size="small"
              color="primary"
              disabled={disabled}
              onClick={onContactClick}
              sx={{
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                ml: 0.25,
              }}
            >
              <ContactSupportOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
    </Stack>
  );
}
